import { useEffect, useRef, useState } from 'react';
import { upperMantendoCursor } from '../inputs/UpperInput';
import { Search, UserPlus, Loader2, Phone } from 'lucide-react';
import { mascaraTelefoneBR, nacionalTelefoneBR, formatTelefoneBR } from '@fiodecobre/shared';
import { api, ApiError } from '../../lib/api';
import { useListaTeclado } from '../../lib/useListaTeclado';
import { useToast } from '../Toast';
import { Avatar } from '../Avatar';

interface ClienteRow {
  id: number;
  nome: string;
  razao_social: string | null;
  tipo_pessoa: string | null;
  codigo_legado: string | null;
  cpf_cnpj: string | null;
  cidade: string | null;
  uf: string | null;
  consignacao?: boolean;
  /** Foto da ficha (migration 0282, [DA-697]) — caminho nosso, copiado do contato do WhatsApp. */
  foto_url?: string | null;
  whatsapp?: string | null;
  telefone?: string | null;
}

// Os numeros da coluna Telefone: o WhatsApp primeiro, o telefone quando nao ha WhatsApp, e os
// DOIS quando sao numeros diferentes. Medido em 02/11/2026 nas 584 fichas ativas: 186 tem os dois
// campos, e so' 11 deles com numeros diferentes — nas outras 175 o segundo seria repeticao.
function telefonesDoCliente(c: ClienteRow): string[] {
  const zap = nacionalTelefoneBR(c.whatsapp);
  const fone = nacionalTelefoneBR(c.telefone);
  const out: string[] = [];
  if (zap) out.push(formatTelefoneBR(c.whatsapp));
  if (fone && fone !== zap) out.push(formatTelefoneBR(c.telefone));
  return out;
}

interface ModalBuscarClienteProps {
  onSelect: (c: ClienteRow) => void;
  onClose: () => void;
}

// Tira acento (mesma regra de Clientes: nomes ficam sem acento pra facilitar a busca).
function semAcento(s: string): string {
  return s.normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');
}

export function ModalBuscarCliente({ onSelect, onClose }: ModalBuscarClienteProps) {
  const toast = useToast();
  const [busca, setBusca] = useState('');
  const [lista, setLista] = useState<ClienteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [cadastrando, setCadastrando] = useState(false);
  const [whatsapp, setWhatsapp] = useState('');
  const [duplicado, setDuplicado] = useState<ClienteRow | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const teclado = useListaTeclado(lista, (c) => onSelect(c));

  const nomeNovo = semAcento(busca.trim());

  // ⛔ O WhatsApp digitado e' CONFERIDO no WhatsApp antes de cadastrar ([DA-814] p.8, pedido dele em
  // 15/10/2026) — a mesma pergunta do caminho 2 do balcao. Numero que nao existe trava o Cadastrar
  // e DIZ por que; "nao deu pra conferir" (WhatsApp da loja fora do ar) deixa seguir. O servidor
  // recusa de novo, entao a tela so' antecipa a resposta.
  const digitosZap = whatsapp.replace(/\D/g, '');
  const [zap, setZap] = useState<{ digitos: string; existe: boolean; incerto: boolean } | null>(null);
  const [conferindoZap, setConferindoZap] = useState(false);
  useEffect(() => {
    if (digitosZap.length < 10) { setZap(null); setConferindoZap(false); return; }
    let cancelado = false;
    setConferindoZap(true);
    const t = setTimeout(() => {
      api.get<{ existe: boolean; incerto: boolean }>('/api/contatos/checar-numero', { query: { telefone: digitosZap } })
        .then((r) => { if (!cancelado) setZap({ digitos: digitosZap, existe: r.existe, incerto: r.incerto }); })
        .catch(() => { if (!cancelado) setZap({ digitos: digitosZap, existe: true, incerto: true }); })
        .finally(() => { if (!cancelado) setConferindoZap(false); });
    }, 500);
    return () => { cancelado = true; clearTimeout(t); };
  }, [digitosZap]);
  const zapAtual = zap && zap.digitos === digitosZap ? zap : null;
  const semWhatsApp = !!zapAtual && !zapAtual.incerto && !zapAtual.existe;

  const podeCadastrar = nomeNovo.length >= 2 && !semWhatsApp && !(digitosZap.length >= 10 && conferindoZap);

  // "Cadastrar": cria o cliente com o nome digitado e JÁ o devolve pro fluxo que abriu a busca
  // (Receber Avulso / Orçamento), sem abandonar a tela.
  //
  // ⚠️ Ate 03/09/2026 gravava SO O NOME, de proposito ("os demais dados ficam pra enriquecer
  // depois em Clientes"). Esse "depois" nunca aconteceu: medido em prod, 1.404 dos 1.638
  // orcamentos parados apontam pro Consumidor e a base tinha 297 clientes pra 1.058 vendas de
  // balcao em 90 dias — sem telefone nao ha como retomar orcamento nenhum. O campo WhatsApp
  // entrou aqui porque este e' o ponto onde o cliente NASCE; enriquecer depois nao acontece.
  //
  // Segue sem travar: numero vazio cadastra igual. Quem exige o numero e' a trava por valor
  // na saida do orcamento, que e' onde ela custa menos ao balcao.
  async function cadastrar() {
    if (!podeCadastrar || cadastrando) return;
    setCadastrando(true);
    setDuplicado(null);
    try {
      const novo = await api.post<ClienteRow>('/api/clientes', {
        nome: nomeNovo,
        whatsapp: whatsapp.replace(/\D/g, '') || undefined,
      });
      toast.sucesso(`Cliente "${novo.nome}" cadastrado`);
      onSelect(novo);
    } catch (e: any) {
      // O 409 de telefone repetido nao e' erro de digitacao — e' o cadastro que JA EXISTE.
      // Mostrar o nome e deixar usar num clique evita o duplicado que a busca por nome nao
      // acha (mesmo cliente escrito de dois jeitos).
      const corpo = e instanceof ApiError ? e.corpo : null;
      if (corpo?.erro === 'whatsapp_duplicado' && corpo.cliente_existente) {
        setDuplicado(corpo.cliente_existente as ClienteRow);
      } else {
        toast.erro(e?.message ?? 'Falha ao cadastrar o cliente');
      }
    } finally {
      setCadastrando(false);
    }
  }

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get<ClienteRow[]>('/api/clientes', {
          query: { busca: busca || undefined },
        });
        setLista(r);
      } catch {
        setLista([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [busca]);

  return (
    <div className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4">
      <div
        className="rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        style={{ backgroundColor: 'var(--panel-bg)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-bold">Buscar cliente</h3>
          <button type="button" className="btn-fechar-modal" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div className="px-5 py-3 border-b border-gray-200 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={busca}
                onChange={(e) => setBusca(upperMantendoCursor(e))}
                onKeyDown={teclado.onKeyDown}
                placeholder="Nome, CPF/CNPJ, codigo..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                style={{ backgroundColor: 'var(--input-bg)', color: 'var(--input-fg)' }}
              />
            </div>
            <div className="relative w-44">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                inputMode="numeric"
                value={whatsapp}
                onChange={(e) => { setWhatsapp(mascaraTelefoneBR(e.target.value)); setDuplicado(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') void cadastrar(); }}
                placeholder="WhatsApp"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                style={{ backgroundColor: 'var(--input-bg)', color: 'var(--input-fg)' }}
              />
            </div>
            <button
              type="button"
              onClick={() => void cadastrar()}
              disabled={!podeCadastrar || cadastrando}
              title={podeCadastrar ? `Cadastrar "${nomeNovo}" e usar agora` : semWhatsApp ? 'Este numero nao tem WhatsApp' : 'Digite o nome pra cadastrar'}
              className="flex items-center gap-1 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-3 py-2 whitespace-nowrap"
            >
              {cadastrando ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              {podeCadastrar ? `Cadastrar "${nomeNovo}"` : 'Cadastrar'}
            </button>
          </div>
          {duplicado ? (
            <div
              className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm"
              style={{ backgroundColor: '#fde68a', color: '#000' }}
            >
              <span>
                Este WhatsApp ja e' de <strong>{duplicado.nome}</strong>
                {duplicado.codigo_legado ? ` (codigo ${duplicado.codigo_legado})` : ''}.
              </span>
              <button
                type="button"
                onClick={() => onSelect(duplicado)}
                className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 whitespace-nowrap"
              >
                Usar este cliente
              </button>
            </div>
          ) : semWhatsApp ? (
            // Botao travado DIZ por que (regra da casa) — e com o mesmo texto do caminho 2 do balcao.
            <span className="text-xs font-semibold" style={{ color: '#b91c1c' }}>
              Este numero nao tem WhatsApp. Confira os digitos — sem WhatsApp ele nao recebe as promocoes.
            </span>
          ) : digitosZap.length >= 10 && conferindoZap ? (
            <span className="text-xs text-gray-500">Conferindo o numero no WhatsApp...</span>
          ) : zapAtual?.incerto ? (
            <span className="text-xs" style={{ color: '#92400e' }}>
              Nao deu pra conferir no WhatsApp agora — da' pra cadastrar assim mesmo.
            </span>
          ) : zapAtual?.existe ? (
            <span className="text-xs text-gray-600">✓ Tem WhatsApp</span>
          ) : nomeNovo.length < 2 ? null : digitosZap.length === 0 ? (
            <span className="text-xs text-gray-500">
              Sem o WhatsApp nao da' pra mandar o orcamento pra ele depois.
            </span>
          ) : null}
        </div>

        <div ref={teclado.listaRef} className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center text-gray-400 py-12 text-sm">Carregando...</div>
          ) : lista.length === 0 ? (
            <div className="text-center text-gray-400 py-12 text-sm">Nenhum cliente encontrado</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0" style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-20">Cod</th>
                  {/* Coluna da foto — pedido dele em 09/09/2026, com o retangulo do print
                      exatamente neste espaco, entre Cod e Nome. Sem rotulo: cabecalho pra uma
                      imagem de 28px seria mais largo que a propria coluna. */}
                  <th className="px-1 py-2 font-medium w-9" aria-label="Foto" />
                  <th className="text-left px-3 py-2 font-medium">Nome</th>
                  {/* Telefone entre Nome e CPF/CNPJ — pedido dele em 02/11/2026, com o retangulo do
                      print neste espaco: separar homonimos (cinco WESLEY na mesma busca). */}
                  <th className="text-left px-3 py-2 font-medium w-40">Telefone</th>
                  <th className="text-left px-3 py-2 font-medium w-40">CPF/CNPJ</th>
                  <th className="text-left px-3 py-2 font-medium w-40">Cidade/UF</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((c, idx) => (
                  <tr
                    key={c.id}
                    data-idx={idx}
                    onClick={() => onSelect(c)}
                    onDoubleClick={() => onSelect(c)}
                    onMouseEnter={() => teclado.setHighlight(idx)}
                    className="cursor-pointer border-b border-gray-100"
                    style={{
                      backgroundColor: idx === teclado.highlight
                        ? 'rgba(59,130,246,0.12)'
                        : idx % 2 === 0 ? 'var(--grid-row-bg)' : 'var(--grid-row-alt-bg)',
                      // Texto todo em preto — pedido dele em 30/10/2026 (o cinza do codigo, da razao
                      // social, do CNPJ e da cidade ficava apagado sobre o azul da linha).
                      color: '#000',
                    }}
                  >
                    <td className="px-3 py-2 font-bold no-underline">
                      {c.codigo_legado ? String(c.codigo_legado).padStart(4, '0') : String(c.id).padStart(4, '0')}
                    </td>
                    {/* ⚠️ O `<Avatar>` da casa cai nas INICIAIS sozinho quando nao ha foto (ou
                        quando ela falha), entao a coluna nunca fica com buraco: hoje so' 54 dos
                        297 clientes tem foto alcancavel, e as outras 243 linhas ganham a bolinha
                        com as iniciais em vez de um vazio.
                        ⛔ Sem `onFotoClick`: a lista existe pra ESCOLHER o cliente, e um clique
                        que amplia a foto roubaria o clique que seleciona a linha. */}
                    <td className="px-1 py-1">
                      <Avatar nome={c.nome} fotoUrl={c.foto_url} tamanho={28} />
                    </td>
                    <td className="px-3 py-2">
                      {c.tipo_pessoa === 'PJ' && c.razao_social ? (
                        <>
                          <span className="font-medium">{c.nome || c.razao_social}</span>
                          {c.nome && c.nome !== c.razao_social && (
                            <span className="ml-2">({c.razao_social})</span>
                          )}
                        </>
                      ) : (
                        c.nome
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {(() => {
                        const fones = telefonesDoCliente(c);
                        return fones.length ? fones.map((f) => <div key={f}>{f}</div>) : '-';
                      })()}
                    </td>
                    <td className="px-3 py-2">{c.cpf_cnpj || '-'}</td>
                    <td className="px-3 py-2">{c.cidade ? `${c.cidade}/${c.uf || ''}` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
